import jsPDF from 'jspdf';

/**
 * PDF font family name constant - uses NotoSans for comprehensive Unicode support
 */
const PDF_FONT_FAMILY = 'NotoSans';

/**
 * Font file sources array containing all NotoSans variants (regular, bold, italic, bold-italic)
 * Maps font files to their corresponding style names for jsPDF registration
 */
const fontSources: Array<{ fileName: string; style: 'normal' | 'bold' | 'italic' | 'bolditalic' }> = [
  { fileName: 'NotoSans-Regular.ttf', style: 'normal' },
  { fileName: 'NotoSans-Bold.ttf', style: 'bold' },
  { fileName: 'NotoSans-Italic.ttf', style: 'italic' },
  { fileName: 'NotoSans-BoldItalic.ttf', style: 'bolditalic' }
];

/**
 * In-memory cache for font file Base64 strings to avoid repeated network requests
 * and conversion operations during PDF generation
 */
const fontBase64Cache = new Map<string, string>();

/**
 * Converts an ArrayBuffer to a Base64 encoded string.
 * Processes the buffer in chunks to avoid stack overflow with large files.
 * @param buffer - The ArrayBuffer to convert
 * @returns Base64 string representation of the buffer
 */

// File TTF (binario) → Base64 (testo) → PDF può usarlo

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  return btoa(binary);
}

/**
 * Fetches a font file from assets and returns it as a Base64 string.
 * Results are cached to avoid redundant network requests and conversions.
 * @param fileName - The name of the font file from /assets/fonts/
 * @returns Promise resolving to the Base64 encoded font data
 * @throws Error if the font file cannot be loaded
 */

/*

fetch("/assets/fonts/NotoSans-Regular.ttf")   Scarica da internet
  ↓
arrayBufferToBase64()                          Converte in testo
  ↓
Salva in memoria (cache)   

*/

async function getFontBase64(fileName: string): Promise<string> {
  const cached = fontBase64Cache.get(fileName);
  if (cached) {
    return cached;
  }

  const response = await fetch(`/assets/fonts/${fileName}`);
  if (!response.ok) {
    throw new Error(`Unable to load PDF font: ${fileName}`);
  }

  const base64 = arrayBufferToBase64(await response.arrayBuffer());
  fontBase64Cache.set(fileName, base64);
  return base64;
}

/**
 * Registers all NotoSans font variants with a jsPDF document to enable Unicode text support.
 * Loads font files from assets, converts them to Base64, and registers them with the PDF engine.
 * Sets the default font family for the document to NotoSans.
 * @param doc - The jsPDF document instance to configure
 * @returns Promise resolving to the PDF font family name (e.g., 'NotoSans')
 */

/*

doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal")  // Registra il font
doc.setFont("NotoSans", "normal")                          // Lo usa

*/
export async function ensureUnicodePdfFont(doc: jsPDF): Promise<string> {
  for (const source of fontSources) {
    const fontBase64 = await getFontBase64(source.fileName);
    (doc as any).addFileToVFS(source.fileName, fontBase64);
    (doc as any).addFont(source.fileName, PDF_FONT_FAMILY, source.style);
  }

  doc.setFont(PDF_FONT_FAMILY, 'normal');
  return PDF_FONT_FAMILY;
}
